/*
 * Description:
 * ---------------------
 * Wi-Fi across homes: each home whose box judges its Wi-Fi, the ones with a
 * device struggling first, and where the trouble is, from the counts each
 * box reports in its heartbeat (server/pkg/homewifi). No device, address or
 * app is ever named here: opening a home shows its own Home Wi-Fi screen.
 * All wording lives in homeWifiWords.js.
 *
 * ! file components/HomeWifiFleetPanel.js
 */
import React, { useCallback, useState } from "react";
import { FiArrowRight } from "react-icons/fi";
import useVisibleInterval from "../hooks/useVisibleInterval";
import { serverApiUrl } from "../serverApi";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import * as W from "./homeWifiWords";
import "./DeviceLimits.css";
import "./DataBudgets.css";
import "./HomeWifi.css";

export default function HomeWifiFleetPanel({ onOpenSite }) {
  const [fleet, setFleet] = useState(null);
  const [absent, setAbsent] = useState(false);
  const read = useCallback(async () => {
    const res = await fetchWithTimeout(serverApiUrl("/homewifi/fleet"), { credentials: "same-origin" }).catch(() => null);
    if (res?.status === 404) setAbsent(true);
    if (!res?.ok) return;
    setFleet(await res.json().catch(() => null));
  }, []);
  useVisibleInterval(read, 30000);
  if (absent) return <div className="panel"><div className="panel-sub">This control plane does not gather homes' Wi-Fi yet.</div></div>;
  if (!fleet) return <div className="panel"><div className="panel-sub">Reading the homes…</div></div>;
  const customers = fleet.customers || [];
  const head = W.fleetHeadline(customers);
  return (
    <div className="dl hw">
      <div className={`panel db-headline hw-head-${head.tone}`} role="status">{head.text}</div>
      {customers.map((c) => (
        <div key={c.domain} className="panel">
          {customers.length > 1 && <div className="panel-title">{c.domain}</div>}
          {c.homes.map((h) => {
            const tone = h.old ? "off" : h.report.summary.poor ? "bad" : h.struggling ? "warn" : "ok";
            return (
              <div key={h.node} className={`hw-device hw-${tone}`}>
                <div className="dl-row">
                  <span className={`hw-dot hw-${tone}`} aria-hidden="true" />
                  <div className="dl-row-main">
                    <strong>{h.node}</strong>
                    <span>{h.old ? `Last heard ${ago(h.last_seen)}: ` : ""}{W.homeSentence(h)}</span>
                  </div>
                  {onOpenSite && (
                    <button type="button" className="dl-btn" onClick={() => onOpenSite(h.node)} aria-label={`Open ${h.node}'s Home Wi-Fi`}>
                      Open <FiArrowRight size={13} />
                    </button>
                  )}
                </div>
                {W.homeExtras(h).map((x, i) => <p key={i} className="hw-also">{x}</p>)}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function ago(at) {
  const s = (Date.now() - new Date(at).getTime()) / 1000;
  if (!(s >= 0)) return "a while ago";
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} days ago`;
}
